// FileUpload.tsx
import React, { useState } from "react";
import {
  Container,
  Typography,
  Paper,
  CircularProgress,
  Dialog,
  DialogContent,
  CircularProgressProps,
} from "@mui/material";
import Box from "@mui/material/Box";
import styled from "@emotion/styled";

import Cookies from "js-cookie";

import Proviso from "../../components/Proviso/Proviso";
import { getExtension, hasProblematicChars, sanitizeFileName } from "../../components/Utils/utils";
import axiosConfig from "../../axiosConfig";

// redux
import { useAppDispatch } from "../../app/hooks";
import {
  estimationAmountAsync,
} from "../../features/estimationAmount/estimationAmountSlice";
import { isEstimationAmountDisplay } from "../../features/isEstimationAmountDisplay/isEstimationAmountDisplaySlice";
import {
  fileSelected,
} from "../../features/isFileSelected/isFileSelectedSlice";
import {
  addEstimationToList,
} from "../../features/estimationAmountList/estimationAmountListSlice";
import {
  addFailedFiles,
  FailedFile,
} from "../../features/failedFiles/failedFilesSlice";
import { useNavigate } from "react-router-dom";
import { changeProcess } from "../../features/managementProcess/managementProcessSlice";
import { UploadIcon } from "./UploadIcon";
import { showSnackbar } from "../../features/snackbar/snackbarSlice";
import {
  getS3PresignedUrl,
  getS3ObjectPresignedUrl,
  uploadFileToS3,
} from "../../services/s3Service";

// スタイル付きのダイアログ
const StyledDialog = styled(Dialog)`
  text-align: center;
`;

const FileUpload: React.FC = () => {
  // redux
  const dispatch = useAppDispatch();
  // fileUpload
  const [numberFilesImport, setNumberFilesImport] = useState(0);
  // DialogState
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [progressLoading, setProgressLoading] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const navigate = useNavigate();

  const fetchEstimateData = async (filesToProcess: File[]) => {
    // ローディング画面を表示
    setIsDialogOpen(true);
    setProgressLoading(0);
    // 表示フラグをfalseに変更
    dispatch(isEstimationAmountDisplay(false));
    dispatch(changeProcess("order"));

    let response: any = null;
    let progressLoadingStatus = 0;
    let invalidEstimateCount = 0;
    const failed: FailedFile[] = [];

    const resetData = () => {
      setIsDialogOpen(false);
      setProgressLoading(0);
      setNumberFilesImport(0);
    };

    const finalize = () => {
      resetData();
      if (failed.length > 0) {
        dispatch(addFailedFiles(failed));
        persistFailedEstimates(failed);
      }
      if (filesToProcess.length > 0) {
        navigate("estimate-delivery-plan");
      }
    };

    // 自動見積不可ファイルを DB に永続化し、案件一覧（履歴）に残す。
    // ログイン済みの場合のみ送信し、失敗しても見積フローは止めない（fire-and-forget）。
    const persistFailedEstimates = (failedFiles: FailedFile[]) => {
      if (!Cookies.get("access_token")) return;
      axiosConfig
        .post("/failed-estimates", {
          items: failedFiles.map((f) => ({
            file_name: f.fileName,
            s3_file_name: f.s3FileName,
            // サムネイルは表示時に presigned URL を都度再生成するため、
            // 期限切れになる presigned URL ではなく S3 キーを保存する
            // （drawings.s3_url_image と同じ規約）。
            s3_url_image: f.s3FileName,
          })),
        })
        .catch((err) => {
          console.error("見積不可ファイルの保存に失敗しました。", err);
        });
    };

    for (let i = 0; i < filesToProcess.length; i++) {
      const file = filesToProcess[i];
      // 現在のタイムスタンプを取得
      const timestamp = new Date().getTime();
      // ファイル名に問題のある文字が含まれている場合は自動リネーム
      const safeName = hasProblematicChars(file.name) ? sanitizeFileName(file.name, i) : file.name;
      // ファイル名にタイムスタンプを追加してユニークなキーを作成
      const uniqueFileName = `${timestamp}-${safeName}`;
      let urlImage: string | undefined;

      try {
        // S3アップロードを実行
        const presignedUrl = await getS3PresignedUrl(uniqueFileName);
        await uploadFileToS3(file, presignedUrl);
        urlImage = await getS3ObjectPresignedUrl(uniqueFileName);
        const extension = getExtension(file.name);

        response = await dispatch(
          estimationAmountAsync({
            fileName: uniqueFileName,
            fileNameOrigin: file.name,
            extension: extension,
            urlImage: urlImage,
          })
        );

        if (estimationAmountAsync.fulfilled.match(response)) {
          if (response.payload.estimationAmount <= 0) {
            invalidEstimateCount++;
            failed.push({
              fileName: file.name,
              s3FileName: uniqueFileName,
              urlImage,
              checkBox: true,
            });
          } else {
            dispatch(
              addEstimationToList({
                ...response.payload,
              })
            );
          }
        }

        if (estimationAmountAsync.rejected.match(response)) {
          invalidEstimateCount++;
          failed.push({
            fileName: file.name,
            s3FileName: uniqueFileName,
            urlImage,
            checkBox: true,
          });
        }

        // 進捗更新
        if (progressLoadingStatus + 1 === filesToProcess.length) {
          finalize();
        } else {
          setProgressLoading(
            ((progressLoadingStatus + 1) / filesToProcess.length) * 100
          );
          progressLoadingStatus++;
        }
      } catch (err) {
        console.error("ファイルのアップロードに失敗しました。", err);
        failed.push({
          fileName: file.name,
          s3FileName: uniqueFileName,
          urlImage,
          checkBox: true,
        });
        invalidEstimateCount++;
        if (progressLoadingStatus + 1 === filesToProcess.length) {
          finalize();
        } else {
          setProgressLoading(
            ((progressLoadingStatus + 1) / filesToProcess.length) * 100
          );
          progressLoadingStatus++;
        }
      }
    }
  };

  const handleFileChange = (files: FileList | null) => {
    if (files) {
      setNumberFilesImport(files.length);
      if (files.length > 50) {
        alert("一度にアップロードできるファイルは最大50枚の図面です。");
      } else {
        let successCount = 0;
        let failureCount = 0;
        let tiffCount = 0;
        let renameCount = 0;
        const validFiles: File[] = [];

        Object.keys(files).forEach((key) => {
          const file = files[Number(key)];
          const extension = getExtension(file.name).toLowerCase();
          if (extension === "png" || extension === "jpg") {
            if (hasProblematicChars(file.name)) {
              renameCount++;
            }
            validFiles.push(file);
            dispatch(fileSelected({ selectedFileName: file.name }));
            successCount++;
          } else if (extension === "tiff" || extension === "tif") {
            tiffCount++;
          } else {
            failureCount++;
          }
        });

        if (successCount > 0) {
          dispatch(
            showSnackbar({
              type: "success",
              message: renameCount > 0
                ? `${successCount}ファイルがアップロードされました（${renameCount}件のファイル名が自動変換されます）`
                : `${successCount}ファイルがアップロードされました`,
            })
          );
        }

        if (tiffCount > 0) {
          alert(
            "TIFF形式のファイルは現在対応しておりません。PNG または JPG 形式に変換してアップロードしてください。"
          );
        }

        if (failureCount > 0) {
          alert(
            "サポートされていないファイル形式です。PNGまたはJPGファイルを選択してください。"
          );
        }

        // 有効なファイルがあれば自動で見積実行
        if (validFiles.length > 0) {
          fetchEstimateData(validFiles);
        }
      }
    } else {
      alert("ファイルをアップロードしてください");
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    const files = event.dataTransfer.files;

    // ドラッグ&ドロップ特有のフィードバック
    if (files.length > 0) {
      handleFileChange(files);
    }
  };

  const content = `
    アップロードできるデータ容量は1ファイル1GBまで<br/>
    アップロードできるファイルはjpgまたはpngの画像ファイルのみです。（TIFF形式には対応しておりません）<br/>
    圧縮ファイルをアップロードしても見積もりはできません。<br/>
    パスワード保護されているファイルは受付できません。<br/>
    jpgまたはpngファイル以外のファイルの場合お問い合わせ窓口へご連絡ください。<br/>
    日本語や特殊文字を含むファイル名は自動変換されます。<br/>
    ファイルを選択すると自動的に見積処理が開始されます。<br/>
    ブラウザやページを閉じるまたは更新動作を行うとアップロードデータ、見積データは削除されます。<br/>
    <br>
    <b>アップロードが失敗する場合</b><br/>
    ・別のブラウザでお試しください。<br/>
    ・ブラウザのキャッシュを削除してからお試しください。<br/>
    上記を実施してもアップロードに失敗する場合はお問い合わせ窓口へその旨お伝えください。<br/>
  `;

  const CircularProgressWithLabel = (
    props: CircularProgressProps & { value: number }
  ) => {
    return (
      <Box sx={{ position: "relative", display: "inline-flex" }}>
        <CircularProgress variant="determinate" {...props} />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: "absolute",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            variant="caption"
            component="div"
            sx={{ color: "text.secondary" }}
          >{`${Math.round(props.value)}%`}</Typography>
        </Box>
      </Box>
    );
  };
  return (
    <div>
      <Container>
        <Paper
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            padding: 1,
            textAlign: "center",
            backgroundColor: isDragOver ? "#e3f2fd" : "#EDEDED",
            border: isDragOver ? "3px solid #1976d2" : "2px dashed #aaa",
            borderRadius: 2,
            transition: "all 0.2s ease",
            boxShadow: isDragOver
              ? "0 4px 12px rgba(25, 118, 210, 0.4)"
              : "0 2px 4px rgba(0,0,0,0.1)",
            cursor: "pointer",
            "&:hover": {
              backgroundColor: isDragOver ? "#e3f2fd" : "#f5f5f5",
            },
          }}
        >
          <input
            type="file"
            multiple
            id="fileInput"
            accept=".jpg,.png"
            style={{ display: "none" }}
            onChange={(event) => {
              const files = event.target.files;
              handleFileChange(files);
              event.target.value = "";
            }}
          />
          <label htmlFor="fileInput" style={{ cursor: "pointer" }}>
            <Typography variant="subtitle1" component="div" sx={{ fontWeight: "bold", lineHeight: 1.3 }}>
              ここで最大50ファイルまで同時に選択できます
            </Typography>
            <Typography variant="body2" component="div" sx={{ lineHeight: 1.4 }}>
              画像ファイルをドラッグ＆ドロップ または ここをクリックして選択
              <br />
              対応形式: JPG, PNG ※TIFF形式には対応しておりません
            </Typography>

            {/* UploadIcon を中央に配置 */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                margin: "4px 0",
                transform: isDragOver ? "scale(1.2)" : "scale(1)",
                transition: "transform 0.3s ease",
              }}
            >
              <UploadIcon
                width={36}
                height={36}
                color={isDragOver ? "#1976d2" : "#666"}
              />
            </Box>

            {numberFilesImport > 0 && (
              <Typography
                variant="body2"
                component="div"
                sx={{ fontWeight: "bold" }}
              >
                アップロード中のファイル: {numberFilesImport}
              </Typography>
            )}
          </label>
        </Paper>
        <Proviso content={content} />
        {/* ポップアップスクリーン */}
        <StyledDialog open={isDialogOpen}>
          <DialogContent>
            {/* テキスト */}
            <Typography variant="h6">見積中</Typography>

            {/* プログレスバー */}
            <CircularProgressWithLabel value={progressLoading} />
          </DialogContent>
        </StyledDialog>
      </Container>
    </div>
  );
};

export default FileUpload;
